// Étape finale "À vous de jouer" — formulaire de fin de parcours.
//
// Pré-rempli via le token d'invitation ATS (?invite=, cf. lib/parcoursInvite).
// Le candidat choisit s'il souhaite rencontrer l'équipe :
//   OUI → email au client "Un nouveau profil souhaite vous rencontrer"
//         (+ copie recruteur), avec sa motivation
//   NON → feedback envoyé au recruteur pour améliorer l'expérience
// Les emails sont envoyés par l'API ATS (app.recruteurs.io).

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Send, ThumbsDown, ThumbsUp } from 'lucide-react';
import {
  getInvite,
  fetchInvitePrefill,
  submitInviteResponse,
  submitOrphanResponse,
  uploadCv,
} from '../../../lib/parcoursInvite';
import { supabase } from '../../../lib/supabase';

type Status = 'loading' | 'form' | 'submitting' | 'done' | 'already';

export default function AVousDeJouerBlock() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const slug = companySlug ?? '';

  const [status, setStatus] = useState<Status>('loading');
  const [posteLabel, setPosteLabel] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [souhaite, setSouhaite] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Mode test admin : visible uniquement pour role='admin' (table users)
  const [isAdmin, setIsAdmin] = useState(false);
  const [clientEmailOverride, setClientEmailOverride] = useState('');

  useEffect(() => {
    async function load() {
      // Source 1 — compte candidat du site (créé pour accéder au parcours).
      // Toujours disponible : les étapes exigent une session (RLS).
      let sessionPrenom = '';
      let sessionNom = '';
      let sessionEmail = '';
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const u = session?.user;
        if (u) {
          const md = (u.user_metadata ?? {}) as Record<string, string | undefined>;
          const fullName = md.name ?? md.full_name ?? '';
          sessionEmail = u.email ?? '';
          // first_name/last_name = inscription email ; given/family = LinkedIn OIDC
          sessionPrenom = md.first_name ?? md.given_name ?? fullName.split(' ')[0] ?? '';
          sessionNom =
            md.last_name ?? md.family_name ?? fullName.split(' ').slice(1).join(' ');

          // Mode test admin (même check que les pages /admin)
          const { data: ud } = await supabase
            .from('users')
            .select('role')
            .eq('id', u.id)
            .maybeSingle();
          if (ud?.role === 'admin') setIsAdmin(true);
        }
      } catch {
        // session illisible → on continue avec l'invitation seule
      }

      // Source 2 — invitation ATS (?invite=) : prioritaire car plus riche
      // (LinkedIn + données fiche candidat + intitulé du poste).
      const invite = getInvite(slug);
      const prefill = invite ? await fetchInvitePrefill(invite) : null;

      if (prefill?.already_responded) {
        setStatus('already');
        return;
      }

      setPrenom(prefill?.candidat.prenom || sessionPrenom);
      setNom(prefill?.candidat.nom || sessionNom);
      setEmail(prefill?.candidat.email || sessionEmail);
      setLinkedin(prefill?.candidat.linkedin_url || '');
      if (prefill?.mission.intitule_poste) setPosteLabel(prefill.mission.intitule_poste);
      setStatus('form');
    }
    load();
  }, [slug]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (souhaite === null) {
      setError('Choisissez une des deux options pour continuer.');
      return;
    }
    if (!prenom.trim() || !nom.trim() || !email.trim()) {
      setError('Prénom, nom et email sont requis.');
      return;
    }

    setStatus('submitting');

    // Upload du CV d'abord (optionnel — on n'échoue pas la réponse si KO)
    let cvUrl: string | null = null;
    if (cvFile) {
      cvUrl = await uploadCv(cvFile);
      if (!cvUrl) {
        setStatus('form');
        setError("L'upload du CV a échoué (PDF, DOC ou DOCX, 10 MB max). Réessayez ou retirez le fichier.");
        return;
      }
    }

    const payload = {
      souhaite_rencontrer: souhaite,
      message: message.trim(),
      contact: {
        prenom: prenom.trim(),
        nom: nom.trim(),
        email: email.trim(),
        linkedin_url: linkedin.trim(),
      },
      ...(cvUrl ? { cv_url: cvUrl } : {}),
      ...(isAdmin && clientEmailOverride.trim()
        ? { client_email_override: clientEmailOverride.trim() }
        : {}),
    };

    const invite = getInvite(slug);
    const ok = invite
      ? await submitInviteResponse(invite, payload)
      : await submitOrphanResponse(slug, payload);

    if (ok) {
      setStatus('done');
    } else {
      setStatus('form');
      setError("L'envoi a échoué. Vérifiez votre connexion et réessayez.");
    }
  }

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-16 text-neutral-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (status === 'already') {
    return (
      <div className="text-center py-10">
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
        <h3 className="text-lg font-bold text-neutral-900 mb-1">
          Vous avez déjà répondu ✓
        </h3>
        <p className="text-sm text-neutral-500">
          Votre réponse a bien été transmise. Votre consultant Recruteurs.io
          revient vers vous rapidement.
        </p>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="text-center py-10">
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
        <h3 className="text-lg font-bold text-neutral-900 mb-1">
          Merci, votre réponse est transmise !
        </h3>
        <p className="text-sm text-neutral-500 max-w-md mx-auto">
          {souhaite
            ? 'Votre profil et votre message viennent d’être transmis. Vous aurez des nouvelles très rapidement pour organiser la rencontre.'
            : 'Merci pour votre franchise — votre retour nous aide à améliorer l’expérience candidat.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-neutral-600 leading-relaxed">
        Vous êtes arrivé au bout du parcours{posteLabel ? (
          <> pour le poste de <strong>{posteLabel}</strong></>
        ) : null}
        . Une dernière chose : dites-nous où vous en êtes.
      </p>

      {/* Coordonnées (pré-remplies si invitation) */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Prénom *
          </label>
          <input
            type="text"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            required
            className="w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm focus:border-[#d03484] focus:outline-none focus:ring-2 focus:ring-pink-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Nom *
          </label>
          <input
            type="text"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            required
            className="w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm focus:border-[#d03484] focus:outline-none focus:ring-2 focus:ring-pink-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Email *
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm focus:border-[#d03484] focus:outline-none focus:ring-2 focus:ring-pink-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Profil LinkedIn
          </label>
          <input
            type="url"
            value={linkedin}
            onChange={(e) => setLinkedin(e.target.value)}
            placeholder="https://linkedin.com/in/…"
            className="w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm focus:border-[#d03484] focus:outline-none focus:ring-2 focus:ring-pink-100"
          />
        </div>
      </div>

      {/* Choix oui / non */}
      <div>
        <p className="mb-2 text-sm font-semibold text-neutral-800">
          Souhaitez-vous rencontrer l&apos;équipe ?
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setSouhaite(true)}
            className={[
              'flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-semibold transition-all',
              souhaite === true
                ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                : 'border-neutral-200 bg-white text-neutral-700 hover:border-emerald-200 hover:bg-emerald-50/40',
            ].join(' ')}
          >
            <ThumbsUp className={`h-5 w-5 shrink-0 ${souhaite === true ? 'text-emerald-500' : 'text-neutral-300'}`} />
            Oui, je souhaite rencontrer l&apos;équipe
          </button>
          <button
            type="button"
            onClick={() => setSouhaite(false)}
            className={[
              'flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-semibold transition-all',
              souhaite === false
                ? 'border-amber-400 bg-amber-50 text-amber-800'
                : 'border-neutral-200 bg-white text-neutral-700 hover:border-amber-200 hover:bg-amber-50/40',
            ].join(' ')}
          >
            <ThumbsDown className={`h-5 w-5 shrink-0 ${souhaite === false ? 'text-amber-500' : 'text-neutral-300'}`} />
            Non, pas pour le moment
          </button>
        </div>
      </div>

      {/* Message conditionnel */}
      {souhaite !== null && (
        <div>
          <label className="mb-1 block text-sm font-semibold text-neutral-800">
            {souhaite
              ? 'En quelques lignes, pourquoi pensez-vous que ce poste vous correspond ?'
              : 'Qu’est-ce qui vous fait hésiter ? Votre retour nous aide à améliorer l’expérience.'}
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder={
              souhaite
                ? 'Votre parcours, vos motivations, ce qui a résonné pendant la découverte…'
                : 'Dites-nous tout, en toute franchise…'
            }
            className="w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-sm focus:border-[#d03484] focus:outline-none focus:ring-2 focus:ring-pink-100"
          />
        </div>
      )}

      {/* CV — proposé quand le candidat veut rencontrer l'équipe */}
      {souhaite === true && (
        <div>
          <label className="mb-1 block text-sm font-semibold text-neutral-800">
            Votre CV <span className="font-normal text-neutral-400">(PDF, DOC — transmis avec votre profil)</span>
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setCvFile(e.target.files?.[0] ?? null)}
            className="block w-full cursor-pointer rounded-xl border border-neutral-200 bg-white text-sm text-neutral-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-[#d03484] file:to-[#d47254] file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-white hover:file:opacity-90"
          />
          {cvFile && (
            <p className="mt-1 text-xs text-emerald-600">
              ✓ {cvFile.name} ({Math.round(cvFile.size / 1024)} Ko)
            </p>
          )}
        </div>
      )}

      {/* Mode test admin — visible uniquement pour le rôle admin du site */}
      {isAdmin && souhaite === true && (
        <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-4">
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-amber-700">
            🧪 Mode test admin — visible uniquement par toi
          </p>
          <p className="mb-2 text-xs text-amber-700">
            Rediriger l&apos;email &quot;Un nouveau profil souhaite vous
            rencontrer&quot; vers cette adresse au lieu du vrai client.
            Sécurité : seuls les emails des membres de l&apos;organisation ATS
            sont acceptés.
          </p>
          <input
            type="email"
            value={clientEmailOverride}
            onChange={(e) => setClientEmailOverride(e.target.value)}
            placeholder="gerald@recruteurs.io (vide = envoi au vrai client)"
            className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
          />
        </div>
      )}

      {error && (
        <p className="rounded-xl bg-rose-50 px-4 py-2.5 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-3 text-sm font-bold text-white shadow-md shadow-[#d03484]/20 hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
      >
        {status === 'submitting' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-4 w-4" />
        )}
        Envoyer ma réponse
      </button>

      <p className="text-xs text-neutral-400">
        🔒 Vos informations restent strictement confidentielles et ne sont
        transmises qu&apos;à votre consultant Recruteurs.io
        {souhaite ? ' et à l’entreprise concernée' : ''}.
      </p>
    </form>
  );
}
